import { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, LogIn, UserPlus, Shield, UserCog } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import Swal from 'sweetalert2';

interface UserProfile {
  user_id: string;
  full_name: string;
  email: string;
  role?: {
    id: string;
    role_name: string;
    created_at: string;
  };
}

interface UserMenuProps {
  isAuthenticated: boolean;
  user: any;
  userProfile: UserProfile | null;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export function UserMenu({ 
  isAuthenticated, 
  user, 
  userProfile, 
  isOpen, 
  onToggle, 
  onClose 
}: UserMenuProps) {
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  useOnClickOutside(userDropdownRef, onClose);

  // Get display name for user
  const getUserDisplayName = () => {
    return userProfile?.full_name || user?.email || 'Usuário';
  };

  // Check if user is admin
  const allowedRoles = ['Admin', 'Super Admin', 'Manager'];
const isAdmin = allowedRoles.includes(userProfile?.role?.role_name);


  const handleLogout = async () => {
    onClose();

    const Toast = Swal.mixin({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });

    try {
      // Obter a sessão atual
      const { data: sessionData } = await supabase.auth.getSession();
      
      // Fazer logout
      await supabase.auth.signOut();
      
      // Limpar tokens do localStorage
      localStorage.removeItem("supabase.auth.token");

      Toast.fire({
        icon: "success",
        title: "Logout realizado com sucesso!"
      });
    } catch (error) {
      console.error("Erro durante logout:", error);
      
      Toast.fire({
        icon: "success",
        title: "Logout realizado com sucesso!"
      });
    }

    // Redirecionar para a página inicial
    navigate("/");
  };

  return (
    <div className="relative" ref={userDropdownRef}>
      <button
        onClick={onToggle}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-700 dark:text-gray-100"
        aria-label="Menu do usuário"
        title="Menu do usuário"
      >
        <User size={20} />
      </button>
      
      {/* User dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 animate-fade-in z-[60]">
          {isAuthenticated ? (
            <>
              {/* Authenticated user section */}
              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                {userProfile?.role && (
                  <div className="absolute top-0 right-0 mt-1 mr-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                      <Shield className="w-3 h-3 mr-1" />
                      {userProfile.role.role_name}
                    </span>
                  </div>
                )}
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Bem vindo(a), 
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {getUserDisplayName()}
                </p>
              </div>
              
              {/* Admin dashboard link - only if user is admin */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100"
                  onClick={onClose}
                >
                  <Shield className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                  <span>Painel Administrativo</span>
                </Link>
              )}

              <Link
                to="/perfil"
                className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100"
                onClick={onClose}
              >
                <UserCog className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Meu perfil</span>
              </Link>
              
              {/* Logout option */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100 text-left cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Sair</span>
              </button>
            </>
          ) : (
            <>
              {/* Unauthenticated user options */}
              <Link
                to="/login"
                className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100"
                onClick={onClose}
              >
                <LogIn className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Entrar</span>
              </Link>
              
              <Link
                to="/registrar"
                className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100"
                onClick={onClose}
              >
                <UserPlus className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Registrar</span>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}